/**
 * V37.46 — Panneau « Rappels automatiques par email » (toggles globaux).
 *
 * Permet au Super Admin d'activer/desactiver en 1 clic chaque categorie
 * de rappels email automatiques, sans redeployer :
 *   - Rappels d'echeance DOS DOMIUM (DESACTIVE par defaut depuis V37.45)
 *   - Rappels d'echeance RCDS BATICO / ESS
 *   - Rappels de renouvellement RCDS / BATICO
 *
 * Endpoints :
 *   GET  /api/admin/email-notification-settings  (lecture admin/super_admin)
 *   PUT  /api/admin/email-notification-settings  (modification super_admin)
 */
import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Bell, BellOff, Loader2, RefreshCw, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOGGLES = [
  {
    key: 'domium_echeance_reminders_enabled',
    label: "Rappels d'échéance DOS DOMIUM",
    description: "Email automatique « Votre échéance de paiement est proche » envoyé au membre J-7, J-3, J-1, J+1.",
    category: 'DOS DOMIUM',
    audit_category: 'domium_echeance',
    color: 'indigo',
    icon: Bell,
  },
  {
    key: 'batico_echeance_reminders_enabled',
    label: "Rappels d'échéance RCDS BATICO / ESS",
    description: "Email automatique de rappel d'échéance pour les souscriptions RCDS BATICO et ESS (J-7, J-3, J-1, J+1).",
    category: 'RCDS BATICO',
    audit_category: 'batico_echeance',
    color: 'emerald',
    icon: Bell,
  },
  {
    key: 'batico_renewal_reminders_enabled',
    label: "Rappels de renouvellement RCDS",
    description: "Email automatique J-30 / J-60 / J-90 avant l'expiration du contrat RCDS pour proposer le renouvellement.",
    category: 'RCDS BATICO',
    audit_category: 'batico_renewal',
    color: 'amber',
    icon: Bell,
  },
];

export default function EmailNotificationToggles({ getAuthHeader }) {
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [stats30d, setStats30d] = useState({});  // V37.48 — counters par categorie
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState({});

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/email-notification-settings`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/email-audit-log/stats?days=30`, { headers: getAuthHeader() }).catch(() => ({ data: { counts: {} } })),
      ]);
      setSettings(settingsRes.data?.settings || {});
      setDefaults(settingsRes.data?.defaults || {});
      setStats30d(statsRes.data?.by_category || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Chargement initial — initialise une seule fois (pattern React 19)
  const fetchedOnce = React.useRef(null);
  if (fetchedOnce.current == null) {
    fetchedOnce.current = true;
    fetchSettings();
  }

  const handleTestSend = async (category) => {
    try {
      const res = await axios.post(
        `${API}/admin/email-notification-settings/test-send`,
        { category },
        { headers: getAuthHeader() },
      );
      if (res.data?.success) {
        toast.success(`Email test envoyé à ${res.data?.to_email}`);
        setTimeout(() => fetchSettings(), 1500);  // recharger stats
      } else {
        toast.warning(res.data?.message || 'Échec envoi test');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi email test');
    }
  };

  const handleToggle = async (key, currentValue) => {
    const newValue = !currentValue;
    setUpdating((p) => ({ ...p, [key]: true }));
    // Optimistic update
    setSettings((prev) => ({ ...prev, [key]: newValue }));
    try {
      const res = await axios.put(
        `${API}/admin/email-notification-settings`,
        { [key]: newValue },
        { headers: getAuthHeader() },
      );
      setSettings(res.data?.settings || {});
      toast.success(
        newValue
          ? `Rappels activés — les emails seront envoyés à partir du prochain cycle (8h00 Europe/Paris)`
          : `Rappels désactivés — plus aucun email ne sera envoyé`
      );
    } catch (err) {
      // Rollback
      setSettings((prev) => ({ ...prev, [key]: currentValue }));
      toast.error(err?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating((p) => ({ ...p, [key]: false }));
    }
  };

  if (loading && !settings) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
          <p className="text-sm text-gray-500 mt-2">Chargement des paramètres...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-indigo-200" data-testid="email-notification-toggles">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-indigo-900">
              <Shield className="h-5 w-5" />
              Rappels automatiques par email
            </CardTitle>
            <CardDescription className="mt-1 text-indigo-700">
              Activer/désactiver en 1 clic chaque catégorie de rappel automatique. Aucun redéploiement nécessaire.
            </CardDescription>
          </div>
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="p-2 rounded-md hover:bg-indigo-100 transition-colors"
            title="Rafraichir"
          >
            <RefreshCw className={`h-4 w-4 text-indigo-700 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="divide-y divide-gray-100 p-0">
        {TOGGLES.map((t) => {
          const value = settings?.[t.key];
          const isUpdating = !!updating[t.key];
          const isDefault = defaults && settings && defaults[t.key] === settings[t.key];
          const Icon = value ? t.icon : BellOff;
          // V37.48 — Compteurs des 30 derniers jours pour cette categorie
          const auditStats = stats30d?.[t.audit_category];
          const sentCount = auditStats?.sent || 0;
          const failedCount = auditStats?.failed || 0;
          return (
            <div
              key={t.key}
              className="flex items-start gap-4 px-6 py-5 hover:bg-gray-50 transition-colors"
              data-testid={`toggle-row-${t.key}`}
            >
              <div className={`mt-1 p-2 rounded-lg ${value ? `bg-${t.color}-100` : 'bg-gray-100'}`}>
                <Icon className={`h-5 w-5 ${value ? `text-${t.color}-700` : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{t.label}</span>
                  <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                  {!isDefault && (
                    <Badge className="bg-blue-700 text-white border-blue-900 text-[10px] font-semibold">Personnalisé</Badge>
                  )}
                  <Badge
                    className={`text-[10px] ${value ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}`}
                  >
                    {value ? 'ACTIVÉ' : 'DÉSACTIVÉ'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mt-1">{t.description}</p>
                {/* V37.48 — Compteur inline temps reel 30j */}
                <div
                  className="mt-2 flex items-center gap-3 text-xs flex-wrap"
                  data-testid={`counters-${t.key}`}
                >
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100">
                    📨 <strong>{sentCount}</strong> envoyé{sentCount > 1 ? 's' : ''} (30j)
                  </span>
                  {failedCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-100">
                      ⚠️ <strong>{failedCount}</strong> échec{failedCount > 1 ? 's' : ''}
                    </span>
                  )}
                  {sentCount === 0 && failedCount === 0 && (
                    <span className="text-gray-400 italic">Aucun envoi sur 30 jours</span>
                  )}
                  {/* V37.49 — Bouton "Tester maintenant" */}
                  <button
                    onClick={() => handleTestSend(t.audit_category)}
                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
                    data-testid={`btn-test-send-${t.audit_category}`}
                    title="Envoyer un email test à votre adresse Super Admin"
                  >
                    🧪 Tester maintenant
                  </button>
                </div>
              </div>
              <div className="shrink-0">
                {isUpdating ? (
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
                ) : (
                  <Switch
                    checked={!!value}
                    onCheckedChange={() => handleToggle(t.key, !!value)}
                    data-testid={`switch-${t.key}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
